/* eslint-disable react-hooks/exhaustive-deps */
import FileUpload from "components/FileUpload";
import { useCourtFilingMarks } from "components/CourtFilingMarks";
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { fetchPoliceStation } from "services/externalUserService";
import {
  addInvestigation2,
  editInvestigation,
  fetchCrimeReferences,
} from "services/incidentService";
import { CrimeReference, Investigation, PoliceStation } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandler, errorHandlerOnForm } from "utils/helper";

type Props = {
  action: "Add" | "Edit";
  crime_reference_id: number;
  selectedFile?: Investigation | null;
  incident?: CrimeReference;
  onClose: (refetch: boolean) => void;
};

const defaultInvestigation: Investigation = {
  uu_id: 0,
  crime_reference_id: null,
  CR_number: "",
  crime_category_id: null,
  investigation_status: null,
  prosecutor_id: null,
  police_id: null,
  assigned_team: "",
  start_date: null,
  description: "",
  investigation_result_naration: "",
  Incident_Registration_number: "",
  asset_reference: "",
  ec_reference_result_file_upload: null,
};
const EconomicInvestigationForm: FC<Props> = ({
  action,
  crime_reference_id,
  selectedFile,
  incident,
  onClose,
}) => {
  const { loginData } = useAuth();
  // Only the director sends the investigation order to the police: the office
  // stamp goes on the attached PDF.
  const policeMarks = useCourtFilingMarks(true);

  const { showTooltip } = useTooltip();
  const [policeStations, setPoliceStations] = useState<PoliceStation[]>([]);
  const [economicInvestigation, setEconomicInvestigation] = useState<
    CrimeReference[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [formState, setFormState] = useState<Investigation>();

  useEffect(() => {
    if (selectedFile) {
      setFormState({
        ...defaultInvestigation,
        ...selectedFile,
      });
    }
  }, [selectedFile]);

  useEffect(() => {
    if (crime_reference_id) {
      setFormState((prev) => ({
        ...(prev ?? defaultInvestigation),
        crime_reference_id: crime_reference_id as any,
      }));
    }
  }, [crime_reference_id]);

  useEffect(() => {
    setLoading(true);
    fetchPoliceStation()
      .then((c: any) =>
        setPoliceStations(
          c?.results?.filter(
            (p: any) =>
              p?.is_police &&
              p?.organization?.admin_unit === loginData?.admin_unit,
          ) || [],
        ),
      )
      // .then(setPoliceStations)
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandler(error),
          "error",
        );
      })
      .finally(() => {
        setLoading(false);
      });

    fetchCrimeReferences()
      .then((data) =>
        setEconomicInvestigation(
          data?.filter(
            (d) =>
              d?.crime_category !== "various_crime" &&
              d?.screening_outcome === "approved",
          ),
        ),
      )
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandler(error),
          "error",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (formState?.uu_id) {
      setFormState({
        ...formState,
        ...(economicInvestigation?.find((d) => d?.uu_id === formState?.uu_id) ||
          defaultInvestigation),
      } as Investigation);
    }
  }, [economicInvestigation, formState?.uu_id]);

  useEffect(() => {
    if (formState?.ec_police_station_id) {
      setFormState((prev) => {
        if (!prev) return prev;
        const stationName =
          policeStations?.find((d) => d?.id === formState.ec_police_station_id)
            ?.organization?.name || "";
        return {
          ...prev,
          ec_police_station_name: stationName,
        };
      });
    }
  }, [policeStations, formState?.ec_police_station_id]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState(
      (prevState) =>
        ({
          ...defaultInvestigation,
          ...(prevState || {}),
          [name]: value,
        }) as Investigation,
    );
  };

  const validateForm = () => {
    if (!formState?.ec_police_station_id) return "Police Station is Required.";
    else if (!formState?.sent_date_to_police) return "Sent Date is Required.";

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setSubmitLoading(true);
    const submitAction =
      action === "Edit" ? editInvestigation : addInvestigation2;

    submitAction({
      crime_reference_id: formState?.crime_reference_id,
      ec_reference_result_files: formState?.ec_reference_result_file_upload,
      ec_police_station_id: formState?.ec_police_station_id,
      ec_police_station_name: formState?.ec_police_station_name,
      sent_date_to_police: formState?.sent_date_to_police,
      report_application_number:
        incident?.report_application_number || undefined,
      incident_application_number:
        incident?.incident_application_number || undefined,
      investigation_status: "Ongoing",
      status: "forwared_to_police",
      ...policeMarks.marks,
    })
      .then(() => {
        showTooltip("Successfully Submitted!", "success");
        setFormState(defaultInvestigation);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail ||
            errorHandlerOnForm(error) ||
            errorHandler(error) ||
            "Something went wrong. Please try again.",
          "error",
        );
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const handleFile = (file: File | null) => {
    setFormState((prev) =>
      prev
        ? { ...prev, ec_reference_result_file_upload: file }
        : { ...defaultInvestigation, ec_reference_result_file_upload: file },
    );
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">{action} Investigation</h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          Lorem Ipsum is a publishing industries as a temporary filler.
        </p>

        {loading ? (
          <Loading />
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-2 gap-x-4 ">
              {/* <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Incident
                </label>
                <select
                  className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                  name="crime_reference_id"
                  value={formState?.crime_reference_id?.toString()}
                  onChange={handleChange}
                >
                  <option value={""}>Select Investigation</option>
                  {economicInvestigation.map((investigation, index) => (
                    <option key={index} value={investigation.uu_id}>
                      Incident RegNo -{" "}
                      {investigation?.Incident_Registration_number}
                    </option>
                  ))}
                </select>
              </div> */}
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Police Station
                </label>
                <select
                  className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                  name="ec_police_station_id"
                  value={formState?.ec_police_station_id}
                  onChange={handleChange}
                >
                  <option value={""}>Select Police Station</option>
                  {policeStations.map((station: any, index) => (
                    <option key={index} value={station.id}>
                      {station?.organization?.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="sent_date_to_police"
                  value={
                    formState?.sent_date_to_police
                      ? new Date(formState.sent_date_to_police)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mt-4">
              <FileUpload
                label="Reference Result File"
                fileTypes="*"
                info="Upload File"
                required={false}
                onChange={handleFile}
              />
            </div>

            {policeMarks.fields}

            <div className="flex justify-end mt-4">
              <button
                type="submit"
                className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                    text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                            ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={loading}
              >
                {submitLoading ? (
                  <div className="flex items-center justify-center">
                    {LoadingIcon}
                    Loading...
                  </div>
                ) : (
                  action + " Investigation"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EconomicInvestigationForm;
